import { zodResolver } from '@hookform/resolvers/zod';
import {
  FirstInvoiceCreationStrategyEnum,
  IInvoiceAutomationSettings,
  InvoiceAutomationSettingsSchema,
  InvoiceCreationEnum,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  MAccordion,
  MBox,
  MButton,
  MCheckbox,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MStack,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { InvoiceDelayInput } from '../../../../components/Monetize/InvoiceDelayInput';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MSettingAccordionItem } from '../../../../components/Monetize/MSettingAccordionItem';
import { MTooltipContentList } from '../../../../components/Monetize/MTooltipContentList';
import {
  SETTING_INVOICE_ACCOUNTING_DATE_OPTIONS,
  SETTING_INVOICE_CREATION_OPTIONS,
} from '../../../../constants/settings';
import { useAccordionControl } from '../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../hooks/useSettingsSectionHighlight';
import { useFlags } from '../../../../services/launchDarkly';
import { INVOICE_DELAY_TOOLTIP } from '../../../../utils/invoiceDelay';
import {
  invoiceBillDateSettingsTooltipItems,
  invoiceBillDateSettingsTooltipTitle,
} from '../../../Invoices/constants';

export type InvoiceAutomationProps = {
  invoiceAutomation: IInvoiceAutomationSettings;
  onSubmit: (data: IInvoiceAutomationSettings) => Promise<void>;
};

export const InvoiceAutomation = ({
  invoiceAutomation,
  onSubmit,
}: InvoiceAutomationProps) => {
  const { showInvoiceAccountingDate } = useFlags();
  const isHighlighted = useSettingsSectionHighlight('invoiceCreation');
  const [isLoading, setIsLoading] = useState(false);
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    reset,
    watch,
  } = useForm<IInvoiceAutomationSettings>({
    resolver: zodResolver(InvoiceAutomationSettingsSchema),
    mode: 'onChange',
    defaultValues: {
      invoiceCreation: InvoiceCreationEnum.AUTOMATIC,
    },
  });

  useEffect(() => {
    if (invoiceAutomation) {
      reset({ ...invoiceAutomation });
    }
  }, [invoiceAutomation, reset]);

  const handleSubmitAutomationConfig = async (
    data: IInvoiceAutomationSettings,
  ) => {
    try {
      setIsLoading(true);
      await onSubmit(data);
    } catch (err: unknown) {
      handleApiErrorToast(err);
    } finally {
      setIsLoading(false);
    }
  };

  const invoiceCreation = watch('invoiceCreation');

  return (
    <MStack w="100%" data-testid="invoice-automation-config">
      <form
        onSubmit={handleSubmit(handleSubmitAutomationConfig)}
        data-testid="invoice-automation-settings-form"
      >
        <MAccordion
          collapsible
          multiple
          value={accordionValue}
          onValueChange={({ value }) => handleAccordionChange(value)}
        >
          <MSettingAccordionItem
            value="0"
            id="invoiceCreation"
            description="Define Invoice creation settings for newly created Bill Groups."
            label="Invoice Creation"
            {...getHighlightStyles(isHighlighted)}
            borderWidth="1px"
          >
            <MBox mt={2}>
              <MGrid templateColumns="repeat(12, 1fr)" gap={2} rowGap={2}>
                <MGridItem colStart={1} colSpan={10}>
                  <MFormField
                    error={errors?.invoiceCreation}
                    label="Create Invoices"
                    onClick={(ev) => ev.stopPropagation()}
                    labelProps={{ mb: 1 }}
                  >
                    <Controller
                      name="invoiceCreation"
                      control={control}
                      render={({ field: { ...rest } }) => (
                        <MCustomSelect
                          items={SETTING_INVOICE_CREATION_OPTIONS}
                          isDisabled={isLoading}
                          {...rest}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
                <MGridItem colStart={1} colSpan={12}>
                  <MFormField
                    error={errors?.firstInvoiceCreationStrategy}
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <Controller
                      name="firstInvoiceCreationStrategy"
                      control={control}
                      render={({ field: { onChange, value, ...rest } }) => (
                        <MCheckbox
                          isChecked={
                            value ===
                            FirstInvoiceCreationStrategyEnum.GENERATE_ON_QUOTE_PROCESSED
                          }
                          {...rest}
                          onChange={(ev) => {
                            if (ev.target.checked) {
                              onChange(
                                FirstInvoiceCreationStrategyEnum.GENERATE_ON_QUOTE_PROCESSED,
                              );
                            } else {
                              onChange(
                                FirstInvoiceCreationStrategyEnum.FOLLOW_EXISTING_SETTINGS,
                              );
                            }
                          }}
                          isDisabled={isLoading}
                        >
                          <MTooltip
                            label="Applies to New and Renewal Quotes with Offerings billed in Advance"
                            placement="bottom-end"
                            shouldWrapChildren
                          >
                            <MFlex alignItems="center">
                              <MText>
                                Only for first invoices, create automatically
                                upon quote processing.
                                <MIcon
                                  as={MdInfo}
                                  color="tPurple.base"
                                  ml="1"
                                  w="3"
                                  h="3"
                                  mb="-0.5"
                                />
                              </MText>
                            </MFlex>
                          </MTooltip>
                        </MCheckbox>
                      )}
                    />
                  </MFormField>
                </MGridItem>
                {[InvoiceCreationEnum.AUTOMATIC].includes(invoiceCreation) && (
                  <MGridItem colStart={1} colSpan={10} mt={2}>
                    <MFormField
                      error={errors?.invoiceDelay}
                      label="Offset from Billing Schedule"
                      tooltip={INVOICE_DELAY_TOOLTIP}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <Controller
                        name="invoiceDelay"
                        control={control}
                        render={({
                          field: { value, onChange, onBlur, name },
                        }) => (
                          <InvoiceDelayInput
                            name={name}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            isDisabled={isLoading}
                          />
                        )}
                      />
                    </MFormField>
                  </MGridItem>
                )}
                {showInvoiceAccountingDate && (
                  <MGridItem colStart={1} colSpan={10}>
                    <MFormField
                      error={errors?.accountingDateCalculationStrategy}
                      label="Bill Date Calculation Strategy"
                      tooltip={
                        <MTooltipContentList
                          title={invoiceBillDateSettingsTooltipTitle}
                          listItems={invoiceBillDateSettingsTooltipItems}
                        />
                      }
                      onClick={(ev) => ev.stopPropagation()}
                      labelProps={{ mb: 1 }}
                    >
                      <Controller
                        name="accountingDateCalculationStrategy"
                        control={control}
                        render={({ field: { ...rest } }) => (
                          <MCustomSelect
                            items={SETTING_INVOICE_ACCOUNTING_DATE_OPTIONS}
                            isDisabled={isLoading}
                            {...rest}
                          />
                        )}
                      />
                    </MFormField>
                  </MGridItem>
                )}
                <MGridItem colSpan={12} my={3}>
                  <MDivider />
                </MGridItem>

                <MGridItem colSpan={12}>
                  <MFlex justify="end">
                    <MButton
                      variant="primary"
                      onClick={handleSubmit(handleSubmitAutomationConfig)}
                      isDisabled={isLoading || !isDirty || !isValid}
                      isLoading={isLoading}
                    >
                      Save
                    </MButton>
                  </MFlex>
                </MGridItem>
              </MGrid>
            </MBox>
          </MSettingAccordionItem>
        </MAccordion>
      </form>
    </MStack>
  );
};
